import { prefersReducedMotion } from '@theme/utilities';

/**
 * Marks top-level sections for scroll-reveal and fades them in as they
 * enter the viewport. Scoped to direct children of #MainContent so the
 * sticky header, footer, cart drawer, and modals are never touched.
 */
function initSectionReveal() {
  if (prefersReducedMotion()) return;

  const sections = document.querySelectorAll('#MainContent > .shopify-section');
  if (sections.length === 0) return;

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('jf-in-view');
        observer.unobserve(entry.target);
      }
    },
    { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
  );

  for (const section of sections) {
    section.setAttribute('data-jf-reveal', '');

    const { top, bottom } = section.getBoundingClientRect();
    const alreadyVisible = top < window.innerHeight * 0.9 && bottom > 0;

    if (alreadyVisible) {
      section.classList.add('jf-in-view');
      continue;
    }

    observer.observe(section);
  }
}

/**
 * Condenses the sticky header (smaller logo/padding via .jf-condensed, see
 * jf-brand.css) once the page scrolls past the hero, so the cart/search/
 * account icons stay reachable without taking up full header height forever.
 * Listens on .page-wrapper, which is this theme's actual scroll container
 * (body itself is fixed-height/overflow-hidden at desktop widths).
 */
function initCondensedHeader() {
  const header = document.getElementById('header-component');
  const scrollContainer = document.querySelector('.page-wrapper');
  if (!header || !scrollContainer) return;

  const THRESHOLD = 80;
  let ticking = false;

  const update = () => {
    header.classList.toggle('jf-condensed', scrollContainer.scrollTop > THRESHOLD);
    ticking = false;
  };

  scrollContainer.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    },
    { passive: true }
  );

  update();
}

/**
 * Staggered fade/slide-up entrance for the hero's content column (heading,
 * subheadline, CTA, USP row) and the product showcase, on page load rather
 * than scroll-into-view (the hero is already in the initial viewport, so
 * IntersectionObserver would fire immediately anyway — a plain load-time
 * class toggle is simpler and avoids double-driving the same elements that
 * initSectionReveal also fades in at the section level).
 */
function initHeroEntrance() {
  if (prefersReducedMotion()) return;

  const hero = document.querySelector('[id$="__hero_jVaWmY"] .section-content-wrapper');
  if (!hero) return;

  // The hero is now a flat stack (heading, secondary text, subheadline,
  // button — no nested column group, since the product showcase moved to
  // its own section below), so its direct children are the stagger items
  // themselves rather than containers to descend into further.
  const items = hero.querySelectorAll(':scope > *');
  items.forEach((item, index) => {
    item.classList.add('jf-hero-enter-item');
    item.style.transitionDelay = `${index * 90}ms`;
  });

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      hero.classList.add('jf-hero-entered');
    });
  });
}

/**
 * Scroll-triggered entrance for the product showcase photo (section_
 * showcase, right after the hero/trust bar) — scales and rotates in from
 * a slightly offset resting state as it enters the viewport, then settles
 * into the existing continuous float animation (.jf-hero-showcase__image,
 * jf-brand.css). Restrained, Apple-product-page-style reveal rather than
 * a fake multi-angle rotation sequence: we only have the one product
 * render (jf-hero-comb.png), not the dozens of frames a true scroll-
 * scrubbed 3D rotation needs.
 *
 * The reveal transform lives on the *wrapper* (.jf-hero-showcase), not on
 * .jf-hero-showcase__image itself, specifically to avoid fighting the
 * image's own always-on jf-float keyframe animation — a CSS animation on
 * a property always wins over a transition targeting that same property
 * on the same element, so a transition-based reveal on .jf-hero-
 * showcase__image's transform would just get silently overridden by
 * jf-float the instant it starts. Two separate elements, two independent
 * transforms, no conflict.
 *
 * IntersectionObserver is used (not a scroll listener) so there's zero
 * ongoing cost before or after the reveal fires once — ticking a
 * transform/opacity transition on scroll for a one-time entrance would be
 * strictly more expensive for the same visual result.
 */
function initShowcaseReveal() {
  if (prefersReducedMotion()) return;

  const showcase = document.querySelector('.jf-hero-showcase');
  if (!showcase) return;

  const { top } = showcase.getBoundingClientRect();
  if (top < window.innerHeight * 0.9) {
    showcase.classList.add('jf-hero-showcase--revealed');
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        showcase.classList.add('jf-hero-showcase--revealed');
        observer.unobserve(entry.target);
      }
    },
    { threshold: 0.3 }
  );

  observer.observe(showcase);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initSectionReveal();
    initCondensedHeader();
    initHeroEntrance();
    initShowcaseReveal();
  }, { once: true });
} else {
  initSectionReveal();
  initCondensedHeader();
  initHeroEntrance();
  initShowcaseReveal();
}
